import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Lock, Save, ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { workshopService } from "@/services/workshop.service";
import { getErrorMessage } from "@/services/api";
import { WORKSHOP_REPAIR_RECOMMENDATIONS } from "@/config/constants";
import { useAuth } from "@/contexts/AuthContext";

function Field({ label, children, required }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-medium">
                {label} {required && <span className="text-danger">*</span>}
            </Label>
            {children}
        </div>
    );
}

export function TechnicalReportForm({ workshop }) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const isAdmin = user?.role === "Administrator";
    const isFinalized = workshop?.workshop_status === "aguardando_proposta";
    // Admin can always edit. Technicians can only edit during diagnosis phase.
    const disabled = isAdmin
        ? false
        : workshop?.workshop_status !== "em_diagnostico";
    const r = workshop?.technical_report || {};

    const { register, handleSubmit, reset, watch, setValue, formState: { isDirty } } = useForm({
        defaultValues: {
            problem_found: r.problem_found || "",
            root_cause: r.root_cause || "",
            recommended_solution: r.recommended_solution || "",
            repair_recommendation: r.repair_recommendation || "",
            general_notes: r.general_notes || "",
        },
    });
    useEffect(() => {
        reset({
            problem_found: r.problem_found || "",
            root_cause: r.root_cause || "",
            recommended_solution: r.recommended_solution || "",
            repair_recommendation: r.repair_recommendation || "",
            general_notes: r.general_notes || "",
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workshop?.id]);

    const save = useMutation({
        mutationFn: (payload) => workshopService.updateTechnicalReport(workshop.id, {
            problem_found: payload.problem_found || null,
            root_cause: payload.root_cause || null,
            recommended_solution: payload.recommended_solution || null,
            repair_recommendation: payload.repair_recommendation || null,
            general_notes: payload.general_notes || null,
        }),
        onSuccess: () => {
            toast.success("Laudo técnico salvo.");
            qc.invalidateQueries({ queryKey: ["workshop", workshop.id] });
        },
        onError: (e) => toast.error(getErrorMessage(e, "Falha ao salvar laudo técnico.")),
    });

    return (
        <form onSubmit={handleSubmit(save.mutate)} className="space-y-3" data-testid="technical-report-form">
            <div className="text-[11px] text-muted-foreground">
                Este laudo alimentará automaticamente a Proposta Comercial no próximo módulo.
            </div>
            {isAdmin && isFinalized && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200">
                    <ShieldAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>
                        Diagnóstico já <b>finalizado</b>. Você (administrador) ainda pode editar
                        o laudo para correções — mudanças ficam registradas no histórico.
                    </span>
                </div>
            )}
            <Field label="Problema encontrado">
                <Textarea rows={2} {...register("problem_found")} disabled={disabled} data-testid="report-problem" />
            </Field>
            <Field label="Causa raiz">
                <Textarea rows={2} {...register("root_cause")} disabled={disabled} data-testid="report-root-cause" />
            </Field>
            <Field label="Solução recomendada">
                <Textarea rows={2} {...register("recommended_solution")} disabled={disabled} data-testid="report-solution" />
            </Field>
            <Field label="Recomendação final">
                <Select
                    value={watch("repair_recommendation") || ""}
                    onValueChange={(v) => setValue("repair_recommendation", v, { shouldDirty: true })}
                    disabled={disabled}
                >
                    <SelectTrigger data-testid="report-repair-reco"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                    <SelectContent>
                        {WORKSHOP_REPAIR_RECOMMENDATIONS.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Notas gerais">
                <Textarea rows={2} {...register("general_notes")} disabled={disabled} data-testid="report-general-notes" />
            </Field>

            {/* Internal-only field for the technician's price estimate */}
            <div className="rounded-lg border border-amber-300/60 bg-amber-50/50 p-3 dark:border-amber-700/50 dark:bg-amber-950/30">
                <div className="mb-2 flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" />
                    <span className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                        Preço estimado do serviço
                    </span>
                    <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900 dark:bg-amber-800/40 dark:text-amber-200">
                        Interno
                    </span>
                </div>
                <p className="mb-2 text-[11px] text-amber-900/80 dark:text-amber-200/80">
                    Informe quanto você cobraria pelo serviço. Serve <b>apenas</b> como referência
                    para o Administrador precificar a proposta comercial. <b>Não aparece</b> no PDF
                    enviado ao cliente.
                </p>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">R$</span>
                    <Input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        placeholder="0,00"
                        className="max-w-40"
                        {...register("technician_price_estimate")}
                        disabled={disabled}
                        data-testid="report-technician-price"
                    />
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    type="submit"
                    disabled={disabled || save.isPending || !isDirty}
                    className="bg-secondary text-secondary-foreground"
                    data-testid="report-save-button"
                >
                    <Save className="mr-2 h-4 w-4" /> Salvar laudo
                </Button>
            </div>
        </form>
    );
}

export default TechnicalReportForm;
